import { useMemo, useState } from 'react'
import { Copy, getProjectCurrencySign, t } from '@stokr/components-library'
import { ChartFootnote } from 'components/sto/chart'
import { DonutChart, downloadCsv, shortNumber } from 'components/ui/chart'
import { toCompositionRows, type NavMonth, type NavSlice } from 'models/navComposition'
import { formatUtcMonth } from 'utils/formatUtcDate'
import { OptionMenu } from 'components/sto/OptionMenu'
import { InlineAction } from './Performance.styles'
import {
  CentreDate,
  CentreLabel,
  CentreValue,
  Composition,
  Legend,
  LegendDot,
  LegendLabel,
  LegendList,
  LegendRow,
  LegendValue,
  NavCard,
  NavHeader,
  PeriodButton,
  PeriodRow,
  Total,
  ViewLabel,
} from './NavPanel.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const DONUT_SIZE = 240

/** `$6.7M`, rounded; a currency without a sign reads `1.9K XAUt`, `450.25 XAUt`. */
const shortMoney = (value: number, currency: string): string => {
  const sign = getProjectCurrencySign(currency)
  const minus = value < 0 ? '-' : ''
  // Gold amounts are small: below a thousand, two decimals rather than the raw number.
  const amount = shortNumber(Math.abs(value), { decimals: sign ? undefined : 2 })
  return sign ? `${minus}${sign}${amount}` : `${minus}${amount} ${currency}`
}

/** `$15,656,415`: the whole amount, no decimals; a currency without a sign (XAUt) always two (`3,120.00`). */
const fullMoney = (value: number, currency: string): string => {
  const sign = getProjectCurrencySign(currency)
  const decimals = sign ? 0 : 2
  const amount = value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return sign ? `${sign}${amount}` : `${amount} ${currency}`
}

const sliceText = (slice: NavSlice, currency: string) =>
  `${shortMoney(slice.value, currency)} (${slice.share.toFixed(1)}%)`

interface NavPanelProps {
  /** Newest first; the tab is only offered when there is at least one. */
  months: NavMonth[]
  currency: string
  symbol: string
}

/**
 * Figma 3990:77340: a fund's NAV composition by month. The table behind it is served as the
 * CSV, every month at once.
 */
export const NavPanel = ({ months, currency, symbol }: NavPanelProps) => {
  const [picked, setPicked] = useState<string>()
  const trackSto = useStoTrack()
  // The legend row under the pointer, highlighted in the chart.
  const [hovered, setHovered] = useState<string>()
  // A picked month that new data no longer has falls back to the latest.
  const historical = months.slice(1).some((item) => item.date === picked) ? picked : undefined
  const month = months.find((item) => item.date === historical) ?? months[0]
  const earlier = months.slice(1)
  const monthLabel = formatUtcMonth(month.time) ?? month.date
  const slices = useMemo(
    () => month.slices.map((slice) => ({ ...slice, tooltip: `${slice.label}: ${sliceText(slice, currency)}` })),
    [month, currency],
  )

  const download = () => {
    trackSto('sto_chart_csv_downloaded', { chart: 'nav_composition' })
    downloadCsv(
      toCompositionRows(months, currency),
      t('stoPage.performance.nav.csvFile', { symbol: symbol || 'STOKR' }),
    )
  }

  return (
    <NavCard>
      <NavHeader>
        <ViewLabel>
          <Copy id="stoPage.performance.nav.composition" />
        </ViewLabel>
        <PeriodRow>
          <PeriodButton
            type="button"
            black
            $active={!historical}
            data-cy="sto-nav-period-latest"
            onClick={() => {
              if (historical) trackSto('sto_nav_period_selected', { period: 'latest' })
              setPicked(undefined)
            }}>
            <Copy id="stoPage.performance.nav.latest" />
          </PeriodButton>
          {earlier.length > 0 && (
            <OptionMenu
              label={<Copy id="stoPage.performance.nav.historical" />}
              menuLabel={t('stoPage.performance.nav.monthsLabel')}
              options={earlier.map((item) => ({ value: item.date, label: formatUtcMonth(item.time) ?? item.date }))}
              selected={historical}
              active={Boolean(historical)}
              dataCy="sto-nav-period-historical"
              onSelect={(date) => {
                if (date !== historical) trackSto('sto_nav_period_selected', { period: 'historical', month: date })
                setPicked(date)
              }}
            />
          )}
        </PeriodRow>
      </NavHeader>

      <Composition>
        {/* Keyed by month: a new month redraws the chart with its opening animation. */}
        <DonutChart
          key={month.date}
          slices={slices}
          size={DONUT_SIZE}
          active={hovered}
          description={t('stoPage.performance.nav.chartDescription', { month: monthLabel })}
          centre={
            <>
              <CentreValue>{shortMoney(month.total, currency)}</CentreValue>
              <CentreLabel>
                <Copy id="stoPage.performance.nav.globalNav" />
              </CentreLabel>
              <CentreDate>{monthLabel}</CentreDate>
            </>
          }
        />
        <Legend>
          <LegendList>
            {month.slices.map((slice, index) => (
              <LegendRow
                // Labels come from back-office and need not be unique.
                key={`${index}-${slice.label}`}
                onMouseEnter={() => setHovered(slice.label)}
                onMouseLeave={() => setHovered(undefined)}>
                <LegendDot $color={slice.color} aria-hidden="true" />
                <LegendLabel>{slice.label}</LegendLabel>
                <LegendValue>{sliceText(slice, currency)}</LegendValue>
              </LegendRow>
            ))}
          </LegendList>
          <Total>
            <Copy id="stoPage.performance.nav.total" values={{ value: fullMoney(month.total, currency) }} />
          </Total>
        </Legend>
      </Composition>

      <ChartFootnote asOf={monthLabel}>
        <InlineAction type="button" data-cy="sto-nav-download-csv" onClick={download}>
          <Copy id="stoPage.performance.downloadCsv" />
        </InlineAction>
      </ChartFootnote>
    </NavCard>
  )
}
